import { useCallback, useEffect, useState } from "react";
import { api } from "@main/api/client";
import type { CurrentUser } from "@main/auth/useCurrentUser";
import { InactivityWatcher } from "@main/components/InactivityWatcher";
import { SectorPromptModal } from "@main/components/SectorPromptModal";
import { ExperienceProvider, useExperience } from "@main/context/ExperienceContext";
import { FrameworksProvider, useFrameworks } from "@main/context/FrameworksContext";
import { onAddonsChanged } from "@main/nav/addonsChanged";
import { AppShell } from "@main/nav/AppShell";
import { buildSetupChecklist } from "@main/nav/clientNav";
import type { NavCounts } from "@main/nav/NavCountsContext";
import type { NavModel } from "@main/nav/types";
import { JsyRoutes } from "./JsyRoutes";
import { buildJsyNav } from "./nav/jsyNav";

// ── Jersey edition client shell ───────────────────────────────────────────
// The same AppShell, providers and inactivity sign-out as the main client
// shell (frontend/src/components/ClientLayout.tsx), with the Jersey sidebar
// and routes. No group switcher and no own-organisation strip: consultancy
// staff and group parents work in the main platform.

interface ShellState {
  tierLevel: number | null;
  tprm: boolean;
}

function useShellState(): ShellState {
  const [state, setState] = useState<ShellState>({ tierLevel: null, tprm: false });
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      Promise.all([
        api.get<{ addons: { tprm?: boolean } }>("/organizations/addons").catch(() => null),
        api.get<{ organization: { serviceTier: { level: number } } }>("/organizations/profile").catch(() => null),
      ]).then(([addons, profile]) => {
        if (cancelled) return;
        setState({
          tierLevel: profile?.organization?.serviceTier?.level ?? 1,
          tprm: addons?.addons?.tprm ?? false,
        });
      });
    };
    load();
    const unsubscribe = onAddonsChanged(load);
    return () => { cancelled = true; unsubscribe(); };
  }, []);
  return state;
}

function JsyShell({ user, onAutoSignOut }: { user: CurrentUser; onAutoSignOut: () => void }) {
  const { enrolled } = useFrameworks();
  const { tierLevel, tprm } = useShellState();
  // Guided (the default) gives the short menu; Expert every page that applies.
  const { novice: guided } = useExperience();
  const enrolledKey = Array.from(enrolled).sort().join(",");
  const permissionsKey = user.permissions.join(",");

  const buildModel = useCallback((counts: NavCounts | null): NavModel => {
    const client = counts?.role === "client" ? counts : null;
    return {
      shellKey: "client",
      header: { title: "Abacas Jersey", subtitle: "Cyber Security Compliance" },
      sections: buildJsyNav({
        tierLevel,
        enrolled: new Set(enrolledKey ? enrolledKey.split(",") : []) as ReturnType<typeof useFrameworks>["enrolled"],
        permissions: permissionsKey ? permissionsKey.split(",") : [],
        counts: client,
        tprm,
        guided,
      }),
      // The setup steps are shown on What's next (ActionsPage), for org admins.
      setup: permissionsKey.split(",").includes("org:admin") ? buildSetupChecklist(client?.setup) : undefined,
    };
  }, [tierLevel, tprm, enrolledKey, permissionsKey, guided]);

  return (
    <>
      <InactivityWatcher onExpire={onAutoSignOut} />
      <AppShell
        model={buildModel}
        user={user}
        tierLevel={tierLevel ?? 1}
        moreToolsTo="/more-tools"
        moreToolsSection="account"
        topBar={{ bell: "alerts", deadlineClock: true }}
        userMenu={{ switches: [], showExperienceMode: true, profileTo: "/profile", versionTo: "/whats-new" }}
      >
        <SectorPromptModal />
        <JsyRoutes />
      </AppShell>
    </>
  );
}

export function JsyClientLayout(props: { user: CurrentUser; onAutoSignOut: () => void }) {
  return (
    <ExperienceProvider>
      <FrameworksProvider>
        <JsyShell {...props} />
      </FrameworksProvider>
    </ExperienceProvider>
  );
}
